import clsx from 'clsx'
import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { getValidatedFilters } from './datatable.helper'
import DatatableCol from './DatatableCol'
import DatatableFilter from './DatatableFilter'
import DatatableFilterCol from './DatatableFilterCol'
import DatatableFooter from './DatatableFooter'
import DatatableHeadCol from './DatatableHeadCol'
import DatatableHeadRow from './DatatableHeadRow'
import DatatableLoader from './DatatableLoader'
import DatatableMobileControl from './DatatableMobileControl'
import DatatableMobileSort from './DatatableMobileSort'
import DatatableRow from './DatatableRow'
import { datatableActions, datatableSelectors } from './store'

import { enviromentHelpers } from '@/helper'
import { ReactDatatableComponent } from '@/react-components'
import { useMobile } from '@/react-hooks'
import { TooltipComponent } from '@/shared/tooltip/tooltip.component'

type Props = {
	endpoint: string
	exist: boolean
}

const Datatable = ({ endpoint, exist }: Props) => {
	const dispatch = useDispatch()
	const cols = useSelector(datatableSelectors.getCol)
	const data = useSelector(datatableSelectors.getData)
	const pending = useSelector(datatableSelectors.getPending)
	const showFilter = useSelector(datatableSelectors.getShowFilter)
	const showPagination = useSelector(datatableSelectors.getShowPaginator)
	const showMoreEnabled = useSelector(datatableSelectors.getShowMoreEnabled)

	const { mobile } = useMobile((mobile) => {
		dispatch(datatableActions.updateShowFilter({ showFilter: mobile }))
	})
	const [mobileShowFilters, setMobileShowFilters] = useState(false)
	const [showSort, setShowSort] = useState(false)
	const mediaWidth = window.innerWidth > 0 ? window.innerWidth : window.screen.width

	const fetchData = () => {
		if (exist && enviromentHelpers.isPDF()) {
			if (typeof (window as any).pdfExport !== 'undefined') {
				dispatch(datatableActions.getDataSuccess((window as any).pdfExport))
			}
			return
		}
		dispatch(datatableActions.updateEndpoint({ endpoint }))
		dispatch(datatableActions.getData())
	}

	useEffect(() => {
		TooltipComponent.init()
	}, [pending])

	useEffect(() => {
		fetchData()

		const watchFetchData = ReactDatatableComponent.datatableFetchData$().subscribe(() => {
			fetchData()
		})

		return () => {
			watchFetchData.unsubscribe()
		}
	}, [])

	useEffect(() => {
		dispatch(datatableActions.updateShowFilter({ showFilter: mobile }))
	}, [mobile])

	const handleShowFilters = () => {
		if (!mobile) {
			return
		}
		setShowSort(false)
		setMobileShowFilters(!mobileShowFilters)
	}

	const handleShowSort = () => {
		if (!mobile) {
			return
		}
		setMobileShowFilters(false)
		setShowSort(!showSort)
	}

	return (
		<div className="relative bg-white w-full overflow-x-auto">
			<div>
				<div
					className={clsx({
						'lg:table min-w-full min-h-100px': true,
						'lg:table-fixed lg:w-full': cols.length ? !!cols[0].size : false,
					})}
				>
					<>
						<DatatableHeadRow>
							{cols.map((item) => (
								<DatatableHeadCol
									key={item.dataKey}
									dataKey={item.dataKey}
									headerAlign={item.headerAlign}
									tooltip={item.tooltip}
									mediaWidth={mediaWidth}
								>
									{item.title}
								</DatatableHeadCol>
							))}
						</DatatableHeadRow>
						<DatatableLoader show={pending} />
						<>
							{mobile && <DatatableMobileControl onShowFilters={handleShowFilters} onShowSort={handleShowSort} />}
							{mobile && showSort && <DatatableMobileSort cols={cols} />}
							{showFilter && (
								<DatatableRow>
									{cols.map((item) => (
										<DatatableFilterCol key={item.dataKey} size={item.size} mediaWidth={mediaWidth}>
											{getValidatedFilters(item.filter).map((filter, i) => (
												<DatatableFilter key={i} col={item.dataKey} label={item.title} filter={filter} />
											))}
										</DatatableFilterCol>
									))}
								</DatatableRow>
							)}
							{data.map((item, index) => (
								<DatatableRow key={item.id} even={index % 2 === 0}>
									{cols.map((col) => (
										<DatatableCol
											key={col.dataKey}
											label={col.title}
											dataKey={col.dataKey}
											type={col.type}
											size={col.size}
											filter={col.filter}
											filterKey={col.filterKey}
											value={item.values[col.dataKey].value}
											imageSettings={item.values[col.dataKey].imageSettings}
											action={item.values[col.dataKey].action}
											icon={item.values[col.dataKey].icon}
											color={item.values[col.dataKey].color}
											backgroundColor={item.values[col.dataKey].backgroundColor}
											align={col.align}
											vAlign={col.vAlign}
											tooltip={col.tooltip}
											mediaWidth={mediaWidth}
											showMoreEnabled={showMoreEnabled}
										/>
									))}
								</DatatableRow>
							))}
						</>
					</>
				</div>
			</div>
			{!pending && <DatatableFooter showPagination={showPagination} />}
		</div>
	)
}

export default React.memo(Datatable)
